/**
 * Anomaly Autopsy Auto-Chain — admin dashboard surfacing the chain results.
 *
 * Shows recent autopsies + lets admin run a fresh one on demand. The "Slack
 * message" preview at the bottom is the killer surface — copies straight
 * to clipboard for paste into channels.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Skull, Bot, Activity, GitBranch, Calculator, Copy, ExternalLink, Sparkles, ChevronRight,
} from 'lucide-react';
import BrandTitle from '../../components/BrandTitle';

const API = process.env.REACT_APP_BACKEND_URL;

const VerdictPill = ({ verdict }) => {
  const map = {
    ACCEPT: { bg: 'bg-emerald-500/15', text: 'text-emerald-300', dot: 'bg-emerald-400' },
    REJECT: { bg: 'bg-rose-500/15', text: 'text-rose-300', dot: 'bg-rose-400' },
    REVIEW: { bg: 'bg-amber-500/15', text: 'text-amber-300', dot: 'bg-amber-400' },
  };
  const s = map[verdict] || map.REVIEW;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md ${s.bg} ${s.text} text-[10px] font-bold uppercase tracking-widest`}>
      <span className={`inline-flex h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {verdict}
    </span>
  );
};

export default function AnomalyAutopsy() {
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/api/anomaly-autopsy/recent?limit=20`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => { setRecent(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-6xl mx-auto px-6 py-8" data-testid="anomaly-autopsy-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="inline-flex w-12 h-12 rounded-xl bg-rose-500 bg-gradient-to-br from-rose-500 to-orange-500 items-center justify-center shadow-lg shadow-rose-500/30">
            <Skull className="w-6 h-6 text-white drop-shadow-sm" />
          </div>
          <div>
            <Badge className="bg-rose-500/15 border border-rose-500/30 text-rose-300 text-[10px] font-mono tracking-widest mb-1">WORLD-FIRST</Badge>
            <h1 className="text-3xl font-bold text-slate-900"><BrandTitle>Anomaly Autopsy Auto-Chain</BrandTitle></h1>
          </div>
        </div>
        <p className="text-base text-slate-700 max-w-3xl leading-relaxed">
          When a cost spike fires, no other FinOps tool tells you <span className="text-white">what caused it, who owns it, and whether the fix is worth doing</span> — all in one Slack message. We do.
        </p>
      </div>

      {/* Chain explanation */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20 mb-8" data-testid="autopsy-chain-explanation">
        <CardHeader>
          <CardTitle className="text-lg text-white">The chain — 4 steps, 1 verdict</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { i: 1, t: 'Bisect', d: 'Find the deploy/commit that caused the spike', I: GitBranch, c: 'text-cyan-400' },
              { i: 2, t: 'Causal split', d: 'Growth-driven (good) or inefficiency (bad)?', I: Activity, c: 'text-emerald-400' },
              { i: 3, t: 'Payback math', d: 'Is the fix worth the engineer hours?', I: Calculator, c: 'text-amber-400' },
              { i: 4, t: 'Slack message', d: 'One-line verdict to the right team owner', I: Bot, c: 'text-rose-400' },
            ].map(({ i, t, d, I, c }) => (
              <div key={i} className="rounded-lg border border-emerald-500/20 bg-slate-950 p-4 relative">
                <div className="text-[9px] font-bold tracking-widest text-slate-500 mb-1.5">STEP {i}</div>
                <I className={`w-5 h-5 ${c} mb-2`} />
                <div className="text-sm font-bold text-white">{t}</div>
                <div className="text-xs text-slate-400 mt-1 leading-relaxed">{d}</div>
                {i < 4 && (
                  <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent autopsies */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-rose-400" />
            Recent autopsies
            <span className="text-xs text-slate-500 font-normal">({recent.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[0, 1, 2].map((i) => (
              <div key={i} className="h-20 bg-slate-800 rounded-lg animate-pulse" />
            ))}</div>
          ) : recent.length === 0 ? (
            <div className="text-center py-10" data-testid="autopsy-empty-state">
              <Skull className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-400 mb-1">No autopsies yet</p>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Once a cost anomaly fires on your account, the autopsy auto-runs in the background and shows up here within ~30 seconds.
              </p>
              <Link to="/anomalies" className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-xs mt-3">
                See active anomalies <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recent.map((a, i) => (
                <div key={i} className="rounded-lg border border-emerald-500/20 bg-slate-950 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-bold text-white">{a.resource}</div>
                    <VerdictPill verdict={a.payback?.verdict || 'REVIEW'} />
                  </div>
                  <div className="text-xs text-slate-400 mb-2">{a.recommended_action}</div>
                  {/* Slack message preview */}
                  <div className="mt-3 flex items-center gap-2 rounded-md bg-slate-800 border border-slate-700 px-3 py-2 font-mono text-xs text-slate-400">
                    <span className="flex-1 truncate">{a.slack_message}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copy(a.slack_message, i)}
                      className="h-6 px-2 text-xs"
                      data-testid={`copy-slack-${i}`}
                    >
                      {copied === i ? '✓ Copied' : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
